import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import api, { extractError } from "@/api/client";
import ImagePicker from "@/components/ImagePicker";
import { getCurrentPositionOnce } from "@/hooks/useGeoTracking";
import { Store, Plus, Edit3, Trash2, MapPin, Phone, Clock, X, LocateFixed, Check, PowerOff, Power, Link as LinkIcon, Copy, UtensilsCrossed, KeyRound, UserPlus, RefreshCw, Shield, FileText } from "lucide-react";

const SHOP_TYPES = [
  { value: "restaurant",    label: "مطاعم" },
  { value: "bakery_sweets", label: "مخابز وحلويات" },
  { value: "cafe",          label: "مقاهي" },
  { value: "pharmacy",      label: "صيدليات" },
  { value: "grocery",       label: "مواد غذائية" },
  { value: "coop_market",   label: "تشاركيات" },
  { value: "vegetables",    label: "خضروات وفواكه" },
  { value: "meat_poultry",  label: "لحوم ودواجن" },
  { value: "phones",        label: "هواتف واكسسوارات" },
  { value: "household",     label: "مواد منزلية" },
  { value: "electronics",   label: "إلكترونيات" },
  { value: "clothes",       label: "ملابس وأحذية" },
  { value: "beauty",        label: "تجميل وعطور" },
  { value: "books",         label: "مكتبات وقرطاسية" },
  { value: "sweets",        label: "حلويات (قديم)" },
  { value: "other",         label: "أخرى" },
];

const empty = {
  name: "", address: "", city: "يفرن", phone: "", lat: 32.0665, lng: 12.5303,
  hours: "10:00 - 23:00", active: true, cuisine: "", shop_type: "restaurant", image: "", slug: "",
  owner_email: "", owner_password: "", owner_name: "", owner_permissions: ["orders", "menu", "info"],
  // Per-restaurant delivery pricing override — when custom_pricing is false the global policy applies
  custom_pricing: false,
  base_fee: 5, base_km: 5, per_km: 1,
};

export default function AdminBranches() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | branch object
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [gpsBusy, setGpsBusy] = useState(false);
  const [ownersFor, setOwnersFor] = useState(null); // restaurant object opened for owners modal
  const [activeType, setActiveType] = useState("all"); // "all" | shop_type value — one section shown at a time

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/restaurants?all=true");
      setBranches(data);
    } catch (e) { setErr(extractError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openNew = () => { setForm(empty); setEditing("new"); setErr(""); };
  const openEdit = (b) => {
    const dp = b.delivery_pricing || null;
    setForm({
      name: b.name, address: b.address, city: b.city || "يفرن",
      phone: b.phone || "", lat: b.lat, lng: b.lng,
      hours: b.hours || "10:00 - 23:00", active: b.active,
      cuisine: b.cuisine || "", shop_type: b.shop_type || "restaurant",
      image: b.image || "", slug: b.slug || "",
      custom_pricing: !!dp,
      base_fee: dp ? dp.base_fee : 5,
      base_km: dp ? dp.base_km : 5,
      per_km: dp ? dp.per_km : 1,
    });
    setEditing(b); setErr("");
  };

  const copyLink = async (b) => {
    const url = `${window.location.origin}/r/${b.slug || b.id}`;
    try {
      await navigator.clipboard.writeText(url);
      window.alert("تم نسخ الرابط");
    } catch {
      window.prompt("انسخ الرابط:", url);
    }
  };

  const save = async () => {
    if (!form.name || !form.address) { setErr("الاسم والعنوان مطلوبان"); return; }
    if (form.custom_pricing) {
      if (form.base_fee <= 0 || form.base_km <= 0 || form.per_km < 0) {
        setErr("قيم التسعير يجب أن تكون موجبة"); return;
      }
    }
    setBusy(true); setErr("");
    try {
      const buildDeliveryPricing = () => (
        form.custom_pricing
          ? { base_fee: parseFloat(form.base_fee), base_km: parseFloat(form.base_km), per_km: parseFloat(form.per_km) }
          : null
      );
      if (editing === "new") {
        // For new restaurant, submit owner fields only if all present
        const { custom_pricing, base_fee, base_km, per_km, ...rest } = form;
        const payload = { ...rest };
        const dp = buildDeliveryPricing();
        if (dp) payload.delivery_pricing = dp;
        if (!payload.owner_email || !payload.owner_password) {
          delete payload.owner_email; delete payload.owner_password;
          delete payload.owner_name; delete payload.owner_permissions;
        }
        await api.post("/restaurants", payload);
      } else {
        // For update, strip owner + local UI fields
        const { owner_email, owner_password, owner_name, owner_permissions,
                custom_pricing, base_fee, base_km, per_km, ...cleanForm } = form;
        const dp = buildDeliveryPricing();
        if (dp) {
          cleanForm.delivery_pricing = dp;
        } else if (editing.delivery_pricing) {
          // User turned off custom pricing → tell backend to unset the override
          cleanForm.clear_delivery_pricing = true;
        }
        await api.patch(`/restaurants/${editing.id}`, cleanForm);
      }
      setEditing(null);
      await refresh();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (b) => {
    setBusy(true);
    try {
      await api.patch(`/restaurants/${b.id}`, { active: !b.active });
      await refresh();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`إلغاء تفعيل المطعم "${b.name}"؟ (لن يتم حذفه نهائياً لحفظ سجل الطلبات)`)) return;
    setBusy(true);
    try {
      await api.delete(`/restaurants/${b.id}`);
      await refresh();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const useMyLocation = async () => {
    setGpsBusy(true);
    try {
      const c = await getCurrentPositionOnce();
      setForm((f) => ({ ...f, lat: c.lat, lng: c.lng }));
    } catch (e) { setErr(e.message); }
    finally { setGpsBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <TopBar/>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-brand text-white flex items-center justify-center"><Store size={20}/></div>
            <div>
              <h1 className="text-2xl font-black" style={{fontFamily:'Cairo'}}>إدارة المحلات</h1>
              <div className="text-xs text-[var(--text-secondary)]">{branches.length} فرع · {branches.filter(b => b.active).length} مفعّل</div>
            </div>
          </div>
          <button onClick={openNew} className="px-4 py-2.5 rounded-xl bg-primary-brand text-white font-bold text-sm flex items-center gap-2 talabya-shadow-float" data-testid="add-branch">
            <Plus size={16}/> محل جديد
          </button>
        </div>

        {err && !editing && <div className="bg-red-50 text-red-600 px-4 py-2 rounded-xl text-sm mb-3">{err}</div>}

        {/* Category tabs — pick ONE shop_type at a time. Only shows chips
            for categories that actually have shops (keeps the bar short). */}
        {!loading && branches.length > 0 && (() => {
          const counts = branches.reduce((acc, b) => {
            const k = b.shop_type || "restaurant";
            acc[k] = (acc[k] || 0) + 1;
            return acc;
          }, {});
          const activeTypes = SHOP_TYPES.filter((s) => counts[s.value]);
          return (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="branches-cat-tabs">
              <button
                onClick={() => setActiveType("all")}
                className={`px-3 py-1.5 rounded-full text-xs font-black transition border ${
                  activeType === "all"
                    ? "bg-primary-brand text-white border-primary-brand"
                    : "bg-white text-[var(--text-primary)] border-[var(--border)] hover:border-primary-brand/50"
                }`}
                data-testid="cat-tab-all"
              >
                الكل ({branches.length})
              </button>
              {activeTypes.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setActiveType(s.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-black transition border ${
                    activeType === s.value
                      ? "bg-primary-brand text-white border-primary-brand"
                      : "bg-white text-[var(--text-primary)] border-[var(--border)] hover:border-primary-brand/50"
                  }`}
                  data-testid={`cat-tab-${s.value}`}
                >
                  {s.label} ({counts[s.value]})
                </button>
              ))}
            </div>
          );
        })()}

        {loading ? (
          <div className="grid gap-3">{[...Array(3)].map((_,i)=><div key={i} className="h-24 rounded-2xl shimmer"/>)}</div>
        ) : branches.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center talabya-shadow-soft">
            <Store size={40} className="mx-auto text-[var(--text-secondary)] mb-3"/>
            <div className="text-lg font-bold" style={{fontFamily:'Cairo'}}>لا توجد فروع</div>
            <button onClick={openNew} className="mt-4 px-5 py-2.5 rounded-xl bg-primary-brand text-white font-bold" data-testid="add-first-branch">إنشاء أول محل</button>
          </div>
        ) : (
          (() => {
            // Group by shop_type for accurate per-section rendering. When a
            // single type is active we render one section; when "all" is
            // picked we render every non-empty section back-to-back.
            const groups = {};
            for (const b of branches) {
              const k = b.shop_type || "restaurant";
              (groups[k] = groups[k] || []).push(b);
            }
            const visibleTypes = activeType === "all"
              ? SHOP_TYPES.filter((s) => groups[s.value]?.length).map((s) => s.value)
              : [activeType];

            const empty = visibleTypes.every((t) => !(groups[t]?.length));
            if (empty) {
              return (
                <div className="bg-white rounded-3xl p-8 text-center talabya-shadow-soft" data-testid="branches-empty-category">
                  <Store size={32} className="mx-auto text-[var(--text-secondary)] mb-2"/>
                  <div className="text-sm font-bold" style={{fontFamily:'Cairo'}}>لا توجد محلات في هذا القسم</div>
                </div>
              );
            }
            return visibleTypes.map((t) => {
              const list = groups[t] || [];
              if (!list.length) return null;
              const meta = SHOP_TYPES.find((s) => s.value === t);
              return (
                <section key={t} className="mb-6" data-testid={`branches-section-${t}`}>
                  <div className="flex items-baseline gap-2 mb-2">
                    <h2 className="text-lg font-black text-[var(--text-primary)]" style={{fontFamily:'Cairo'}}>{meta?.label || t}</h2>
                    <span className="text-xs text-[var(--text-secondary)]">({list.length} محل)</span>
                    <span className="relative -bottom-1 w-8 h-[3px] rounded-full bg-primary-brand ms-1"/>
                  </div>
                  <div className="grid gap-3">
                    {list.map((b) => (
              <div key={b.id} className={`bg-white rounded-2xl p-4 talabya-shadow-soft border ${b.active ? "border-[var(--border)]" : "border-red-200 opacity-70"}`} data-testid={`branch-row-${b.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${b.active ? "bg-primary-light text-primary-brand" : "bg-red-50 text-red-500"}`}>
                    <Store size={18}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold" style={{fontFamily:'Cairo'}}>{b.name}</h3>
                      {b.shop_type && b.shop_type !== "restaurant" && (
                        <span className="text-[10px] font-black bg-primary-light text-primary-brand px-2 py-0.5 rounded-full">
                          {SHOP_TYPES.find((s) => s.value === b.shop_type)?.label || b.shop_type}
                        </span>
                      )}
                      {!b.active && <span className="text-[10px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">معطّل</span>}
                    </div>
                    <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1 mt-1"><MapPin size={11}/> {b.address}</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-[var(--text-secondary)]">
                      {b.cuisine && <span className="text-primary-brand font-bold">{b.cuisine}</span>}
                      <span>{b.city}</span>
                      {b.phone && <span className="flex items-center gap-1"><Phone size={11}/> {b.phone}</span>}
                      {b.hours && <span className="flex items-center gap-1"><Clock size={11}/> {b.hours}</span>}
                    </div>
                    {b.slug && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs bg-primary-light rounded-lg px-2 py-1 text-primary-brand font-mono w-fit max-w-full" dir="ltr">
                        <LinkIcon size={11} className="shrink-0"/> <span className="truncate">/r/{b.slug}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => copyLink(b)}
                      className="w-9 h-9 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 flex items-center justify-center"
                      title="نسخ رابط المطعم" data-testid={`copy-link-${b.id}`}>
                      <Copy size={16}/>
                    </button>
                    <Link to={`/admin/shops/${b.id}`}
                      className="w-9 h-9 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 flex items-center justify-center"
                      title="ملف المحل الشامل" data-testid={`profile-${b.id}`}>
                      <FileText size={16}/>
                    </Link>
                    <Link to={`/admin/menu?restaurant=${b.id}`}
                      className="w-9 h-9 rounded-lg bg-primary-light text-primary-brand hover:bg-primary-brand hover:text-white flex items-center justify-center"
                      title="إدارة منيو المطعم" data-testid={`menu-link-${b.id}`}>
                      <UtensilsCrossed size={16}/>
                    </Link>
                    <button onClick={() => setOwnersFor(b)}
                      className="w-9 h-9 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-600 flex items-center justify-center"
                      title="إدارة مالكي المطعم وأكواد الدعوة" data-testid={`owners-${b.id}`}>
                      <KeyRound size={16}/>
                    </button>
                    <button onClick={() => toggleActive(b)} disabled={busy}
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${b.active ? "bg-yellow-50 text-yellow-600 hover:bg-yellow-100" : "bg-green-50 text-green-600 hover:bg-green-100"}`}
                      title={b.active ? "تعطيل" : "تفعيل"} data-testid={`toggle-${b.id}`}>
                      {b.active ? <PowerOff size={16}/> : <Power size={16}/>}
                    </button>
                    <button onClick={() => openEdit(b)} className="w-9 h-9 rounded-lg bg-[var(--surface-alt)] hover:bg-[var(--border)] flex items-center justify-center" data-testid={`edit-${b.id}`}>
                      <Edit3 size={16}/>
                    </button>
                    <button onClick={() => remove(b)} disabled={busy} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center" data-testid={`del-${b.id}`}>
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </div>
              </div>
                    ))}
                  </div>
                </section>
              );
            });
          })()
        )}
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="branch-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-[var(--border)]">
              <h2 className="text-xl font-black" style={{fontFamily:'Cairo'}}>{editing === "new" ? "محل جديد" : "تعديل المحل"}</h2>
              <button onClick={() => setEditing(null)} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center"><X size={16}/></button>
            </div>
            <div className="p-5 flex flex-col gap-3">
              <div>
                <label className="text-sm font-bold block mb-1">اسم المحل *</label>
                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                  data-testid="form-name"/>
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">نوع المحل *</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {SHOP_TYPES.map((st) => (
                    <button key={st.value} type="button"
                      onClick={() => setForm({...form, shop_type: st.value})}
                      className={`py-2 rounded-xl text-xs font-black border-2 transition ${form.shop_type === st.value ? "bg-primary-brand text-white border-primary-brand talabya-shadow-soft" : "bg-white border-[var(--border)] text-[var(--text-secondary)] hover:border-primary-brand/50"}`}
                      data-testid={`form-shop-type-${st.value}`}>
                      {st.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">التخصص / الوصف المختصر</label>
                <input value={form.cuisine} onChange={(e) => setForm({...form, cuisine: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                  placeholder="مثلاً: برجر، مواد تموينية، مثلجات..." data-testid="form-cuisine"/>
              </div>
              <div>
                <label className="text-sm font-bold block mb-2">صورة المحل</label>
                <ImagePicker value={form.image} onChange={(dataUrl) => setForm({...form, image: dataUrl})} label="صورة المحل"/>
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">رابط الوصول (Slug) — <span className="text-[var(--text-secondary)] font-normal">اختياري، سيُولّد تلقائياً</span></label>
                <div className="flex items-center gap-2 bg-[var(--surface-alt)] rounded-xl px-3">
                  <span className="text-xs text-[var(--text-secondary)] font-mono" dir="ltr">/r/</span>
                  <input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})}
                    className="flex-1 px-2 py-2.5 bg-transparent focus:outline-none font-mono text-sm" dir="ltr"
                    placeholder="burger-house" data-testid="form-slug"/>
                </div>
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">العنوان *</label>
                <input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                  data-testid="form-address"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold block mb-1">المدينة</label>
                  <input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                    data-testid="form-city"/>
                </div>
                <div>
                  <label className="text-sm font-bold block mb-1">الهاتف</label>
                  <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right" dir="ltr"
                    data-testid="form-phone"/>
                </div>
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">ساعات العمل</label>
                <input value={form.hours} onChange={(e) => setForm({...form, hours: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                  placeholder="10:00 - 23:00" data-testid="form-hours"/>
              </div>

              <div className="bg-[var(--surface-alt)] rounded-2xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-bold">الإحداثيات الجغرافية</label>
                  <button type="button" onClick={useMyLocation} disabled={gpsBusy}
                    className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg bg-primary-light text-primary-brand hover:bg-primary-brand hover:text-white disabled:opacity-60"
                    data-testid="form-gps">
                    <LocateFixed size={12} className={gpsBusy ? "animate-pulse-dot" : ""}/> {gpsBusy ? "جاري..." : "موقعي الآن"}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" step="0.000001" value={form.lat} onChange={(e) => setForm({...form, lat: parseFloat(e.target.value) || 0})}
                    className="px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-sm" placeholder="Latitude" dir="ltr" data-testid="form-lat"/>
                  <input type="number" step="0.000001" value={form.lng} onChange={(e) => setForm({...form, lng: parseFloat(e.target.value) || 0})}
                    className="px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-sm" placeholder="Longitude" dir="ltr" data-testid="form-lng"/>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mt-1" data-testid="delivery-pricing-section">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.custom_pricing}
                    onChange={(e) => setForm({...form, custom_pricing: e.target.checked})}
                    className="accent-primary-brand w-4 h-4 mt-0.5"
                    data-testid="toggle-custom-pricing"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-blue-900">تسعير توصيل خاص بهذا المحل</div>
                    <div className="text-[11px] text-blue-700 mt-0.5">
                      {form.custom_pricing
                        ? "سيتم استخدام الأسعار أدناه بدلاً من السعر العام."
                        : "إذا تُرك بدون تفعيل، يُطبَّق السعر العام للإدارة."}
                    </div>
                  </div>
                </label>
                {form.custom_pricing && (
                  <div className="mt-3 grid grid-cols-3 gap-2" data-testid="custom-pricing-inputs">
                    <div>
                      <label className="text-[11px] font-bold block mb-1">السعر الأساسي (د.ل)</label>
                      <input
                        type="number" step="0.5" min="0.5"
                        value={form.base_fee}
                        onChange={(e) => setForm({...form, base_fee: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 rounded-lg border border-blue-200 bg-white text-sm text-center font-bold"
                        dir="ltr"
                        data-testid="pricing-base-fee"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold block mb-1">حتى (كم)</label>
                      <input
                        type="number" step="0.5" min="0.5"
                        value={form.base_km}
                        onChange={(e) => setForm({...form, base_km: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 rounded-lg border border-blue-200 bg-white text-sm text-center font-bold"
                        dir="ltr"
                        data-testid="pricing-base-km"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold block mb-1">لكل كم إضافي</label>
                      <input
                        type="number" step="0.25" min="0"
                        value={form.per_km}
                        onChange={(e) => setForm({...form, per_km: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 rounded-lg border border-blue-200 bg-white text-sm text-center font-bold"
                        dir="ltr"
                        data-testid="pricing-per-km"
                      />
                    </div>
                    <div className="col-span-3 text-[11px] text-blue-700 bg-white/70 rounded-lg px-2 py-1.5">
                      المعادلة: {form.base_fee || 0} د.ل حتى {form.base_km || 0} كم، ثم {form.per_km || 0} د.ل لكل كم إضافي
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} className="accent-primary-brand w-4 h-4"/>
                <span className="text-sm font-bold">مفعّل (يظهر للزبائن)</span>
              </label>

              {editing === "new" && (
                <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 mt-1" data-testid="inline-owner">
                  <div className="flex items-center gap-2 mb-2">
                    <KeyRound size={14} className="text-purple-600"/>
                    <div className="text-sm font-bold text-purple-800">مالك المطعم (اختياري)</div>
                  </div>
                  <div className="text-[11px] text-purple-700 mb-3">أدخل بيانات دخول مالك المطعم لإنشاء حسابه مباشرة. يمكنك تركها فارغة وإضافته لاحقاً.</div>
                  <div className="grid grid-cols-1 gap-2">
                    <input placeholder="اسم المالك" value={form.owner_name} onChange={(e) => setForm({...form, owner_name: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl border border-purple-200 bg-white focus:border-purple-500 focus:outline-none text-right" data-testid="owner-name"/>
                    <input placeholder="البريد الإلكتروني" type="email" value={form.owner_email} onChange={(e) => setForm({...form, owner_email: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl border border-purple-200 bg-white focus:border-purple-500 focus:outline-none" dir="ltr" data-testid="owner-email"/>
                    <input placeholder="كلمة المرور (6 أحرف على الأقل)" type="text" value={form.owner_password} onChange={(e) => setForm({...form, owner_password: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl border border-purple-200 bg-white focus:border-purple-500 focus:outline-none" dir="ltr" data-testid="owner-password"/>
                  </div>
                  <div className="mt-2">
                    <div className="text-[11px] font-bold text-purple-800 mb-1">الصلاحيات:</div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {[
                        {k: "orders", l: "الطلبات"},
                        {k: "menu", l: "قائمة الطعام"},
                        {k: "info", l: "بيانات المطعم"},
                      ].map((p) => (
                        <label key={p.k} className="flex items-center gap-1 bg-white rounded-lg px-2 py-1 border border-purple-200 cursor-pointer" data-testid={`perm-${p.k}`}>
                          <input type="checkbox" checked={(form.owner_permissions || []).includes(p.k)}
                            onChange={(e) => {
                              const cur = new Set(form.owner_permissions || []);
                              if (e.target.checked) cur.add(p.k); else cur.delete(p.k);
                              setForm({...form, owner_permissions: [...cur]});
                            }} className="accent-purple-600"/>
                          {p.l}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {err && <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm">{err}</div>}

              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditing(null)} className="flex-1 py-3 rounded-xl bg-[var(--surface-alt)] font-bold text-sm">إلغاء</button>
                <button onClick={save} disabled={busy}
                  className="flex-[2] py-3 rounded-xl bg-primary-brand text-white font-bold text-sm talabya-shadow-float disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="form-save">
                  <Check size={16}/> {busy ? "جاري الحفظ..." : "حفظ"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {ownersFor && (
        <OwnersModal restaurant={ownersFor} onClose={() => setOwnersFor(null)}/>
      )}
    </div>
  );
}

function OwnersModal({ restaurant, onClose }) {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [addForm, setAddForm] = useState({ email: "", password: "", name: "", permissions: ["orders", "menu", "info"] });
  const [showAdd, setShowAdd] = useState(false);
  const [invite, setInvite] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/restaurants/${restaurant.id}/owners`);
      setOwners(data);
    } catch (e) { setErr(extractError(e)); }
    finally { setLoading(false); }
  }, [restaurant.id]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!addForm.email || !addForm.password) { setErr("البريد وكلمة المرور مطلوبان"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/restaurants/${restaurant.id}/owners`, addForm);
      setShowAdd(false);
      setAddForm({ email: "", password: "", name: "", permissions: ["orders", "menu", "info"] });
      await load();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (o) => {
    setBusy(true);
    try { await api.patch(`/admin/restaurants/${restaurant.id}/owners/${o.id}`, { active: !o.active }); await load(); }
    catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
  };

  const togglePerm = async (o, perm) => {
    const cur = new Set(o.permissions || []);
    if (cur.has(perm)) cur.delete(perm); else cur.add(perm);
    setBusy(true);
    try { await api.patch(`/admin/restaurants/${restaurant.id}/owners/${o.id}`, { permissions: [...cur] }); await load(); }
    catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
  };

  const resetPassword = async (o) => {
    const p = window.prompt("كلمة المرور الجديدة (6 أحرف على الأقل):", "");
    if (!p || p.length < 6) return;
    setBusy(true);
    try { await api.patch(`/admin/restaurants/${restaurant.id}/owners/${o.id}`, { password: p }); window.alert("تم إعادة تعيين كلمة المرور"); }
    catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
  };

  const remove = async (o) => {
    if (!window.confirm(`حذف المالك ${o.email} نهائياً؟`)) return;
    setBusy(true);
    try { await api.delete(`/admin/restaurants/${restaurant.id}/owners/${o.id}`); await load(); }
    catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
  };

  const genInvite = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/restaurants/${restaurant.id}/invite`);
      setInvite(data);
    } catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
  };

  const copyInvite = async () => {
    if (!invite) return;
    const url = `${window.location.origin}${invite.invite_url}`;
    try { await navigator.clipboard.writeText(url); window.alert("تم نسخ رابط الدعوة"); }
    catch { window.prompt("انسخ الرابط:", url); }
  };

  const PERM_LABELS = { orders: "الطلبات", menu: "القائمة", info: "بيانات المطعم" };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="owners-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-2xl rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-[var(--border)] z-10">
          <div className="flex items-center gap-2">
            <KeyRound className="text-purple-600" size={20}/>
            <div>
              <div className="font-black text-lg" style={{fontFamily:'Cairo'}}>مالكو {restaurant.name}</div>
              <div className="text-[11px] text-[var(--text-secondary)]">إدارة حسابات المطعم وأكواد الدعوة</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center"><X size={16}/></button>
        </div>
        <div className="p-5">
          {err && <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm mb-3">{err}</div>}

          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={() => setShowAdd(true)} className="px-4 py-2 rounded-xl bg-primary-brand text-white font-bold text-sm flex items-center gap-2" data-testid="btn-new-owner">
              <UserPlus size={14}/> إنشاء مالك
            </button>
            <button onClick={genInvite} disabled={busy} className="px-4 py-2 rounded-xl bg-white border border-purple-300 text-purple-700 font-bold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="btn-gen-invite">
              <KeyRound size={14}/> توليد كود دعوة
            </button>
          </div>

          {invite && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-3" data-testid="invite-box">
              <div className="text-xs text-purple-700 mb-1">شارك هذا الرابط مع مالك المطعم للتسجيل:</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 font-mono text-xs bg-white rounded-lg px-3 py-2 border border-purple-200 truncate" dir="ltr">
                  {window.location.origin}{invite.invite_url}
                </div>
                <button onClick={copyInvite} className="p-2 rounded-lg bg-purple-600 text-white" data-testid="btn-copy-invite"><Copy size={14}/></button>
              </div>
              <div className="text-[10px] text-purple-600 mt-1">الكود: <span className="font-mono">{invite.code}</span> · للاستخدام مرة واحدة</div>
            </div>
          )}

          {showAdd && (
            <div className="bg-[var(--surface-alt)] rounded-xl p-4 mb-4" data-testid="new-owner-form">
              <div className="text-sm font-bold mb-2" style={{fontFamily:'Cairo'}}>إنشاء مالك جديد</div>
              <div className="flex flex-col gap-2">
                <input placeholder="الاسم" value={addForm.name} onChange={(e) => setAddForm({...addForm, name: e.target.value})}
                  className="px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-right" data-testid="new-owner-name"/>
                <input placeholder="البريد" type="email" value={addForm.email} onChange={(e) => setAddForm({...addForm, email: e.target.value})}
                  className="px-3 py-2 rounded-lg border border-[var(--border)] bg-white" dir="ltr" data-testid="new-owner-email"/>
                <input placeholder="كلمة المرور" type="text" value={addForm.password} onChange={(e) => setAddForm({...addForm, password: e.target.value})}
                  className="px-3 py-2 rounded-lg border border-[var(--border)] bg-white" dir="ltr" data-testid="new-owner-password"/>
                <div className="flex flex-wrap gap-2 text-xs">
                  {["orders", "menu", "info"].map(p => (
                    <label key={p} className="flex items-center gap-1 bg-white rounded-lg px-2 py-1 border border-[var(--border)] cursor-pointer">
                      <input type="checkbox" checked={(addForm.permissions || []).includes(p)}
                        onChange={(e) => {
                          const cur = new Set(addForm.permissions || []);
                          if (e.target.checked) cur.add(p); else cur.delete(p);
                          setAddForm({...addForm, permissions: [...cur]});
                        }} className="accent-primary-brand"/>
                      {PERM_LABELS[p]}
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => setShowAdd(false)} className="flex-1 py-2 rounded-lg bg-white border border-[var(--border)] text-sm">إلغاء</button>
                  <button onClick={create} disabled={busy} className="flex-1 py-2 rounded-lg bg-primary-brand text-white font-bold text-sm disabled:opacity-60" data-testid="new-owner-save"><Check size={14} className="inline ml-1"/> إنشاء</button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-sm text-[var(--text-secondary)] text-center py-6">جاري التحميل…</div>
          ) : owners.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center border border-dashed border-[var(--border)]" data-testid="no-owners">
              <Shield size={30} className="mx-auto text-[var(--text-secondary)] mb-2"/>
              <div className="text-sm text-[var(--text-secondary)]">لا يوجد مالكون بعد. أضف مالكاً أو ولّد كود دعوة.</div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {owners.map(o => (
                <div key={o.id} className={`bg-white rounded-xl p-3 border ${o.active ? "border-[var(--border)]" : "border-red-200 opacity-70"}`} data-testid={`owner-${o.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{o.name}</div>
                      <div className="text-xs text-[var(--text-secondary)] truncate" dir="ltr">{o.email}</div>
                      {!o.active && <div className="text-[10px] font-bold text-red-500 mt-0.5">حساب معطّل</div>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => toggleActive(o)} disabled={busy} title={o.active ? "تعطيل" : "تفعيل"}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${o.active ? "bg-yellow-50 text-yellow-600" : "bg-green-50 text-green-600"}`}
                        data-testid={`toggle-owner-${o.id}`}>
                        {o.active ? <PowerOff size={14}/> : <Power size={14}/>}
                      </button>
                      <button onClick={() => resetPassword(o)} disabled={busy} title="إعادة تعيين كلمة المرور"
                        className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center" data-testid={`reset-pw-${o.id}`}>
                        <RefreshCw size={14}/>
                      </button>
                      <button onClick={() => remove(o)} disabled={busy} title="حذف نهائي"
                        className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center" data-testid={`del-owner-${o.id}`}>
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {["orders", "menu", "info"].map(p => (
                      <button key={p} onClick={() => togglePerm(o, p)} disabled={busy}
                        className={`text-[11px] px-2 py-1 rounded-full font-bold transition ${(o.permissions || []).includes(p) ? "bg-primary-brand text-white" : "bg-[var(--surface-alt)] text-[var(--text-secondary)]"}`}
                        data-testid={`perm-btn-${o.id}-${p}`}>
                        {PERM_LABELS[p]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

